import React from "react";
import styled from "@emotion/styled";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

const InputContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "0.8rem",
});

const Input = styled.input<{ maxWidth?: string; error: boolean }>(
  ({ theme, width, height, maxWidth, error }) => ({
    width: width,
    maxWidth: maxWidth ? maxWidth : "",
    height: height,
    border: error
      ? `1px solid ${theme.color.orange}`
      : `1px solid ${theme.color.light_gray}`,
    padding: "1.6rem",
    borderRadius: "0",
  }),
);

const Message = styled.div<{ success: boolean; error: boolean }>(
  ({ theme, success, error }) => ({
    display: "flex",
    alignItems: "center",
    fontSize: "1.2rem",
    lineHeight: "1.2rem",
    gap: "0.4rem",
    color: error
      ? theme.color.orange
      : success
        ? theme.color.green
        : theme.color.light_gray,
  }),
);

interface Props {
  name: string;
  type?: string;
  width?: string;
  height?: string;
  placeholder?: string;
  message?: string;
  value?: string;
  maxWidth?: string;
  mode?:
    | "none"
    | "text"
    | "decimal"
    | "numeric"
    | "tel"
    | "search"
    | "email"
    | "url";
  dir?: "rtl" | "ltr";
  success?: boolean;
  icon?: boolean;
}

const InputWithMessage = ({
  name,
  type = "text",
  placeholder,
  mode = "text",
  dir,
  success = false,
  message = "",
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { formState, watch, register } = useFormContext();

  const errorState = formState.errors[name] !== undefined && watch(name) !== "";

  return (
    <InputContainer>
      <Input
        {...register(name)}
        type={type}
        placeholder={placeholder}
        inputMode={mode}
        dir={dir}
        error={errorState}
      />

      <Message success={success} error={errorState}>
        {t(message)}
        <Image
          src={
            errorState
              ? theme.image.check_orange.default
              : success
                ? theme.image.check_green.default
                : theme.image.check_gray.default
          }
          size="1.6rem"
        />
      </Message>
    </InputContainer>
  );
};

export default InputWithMessage;
